import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import {
  getCurrentUserId,
  getDealApplicationId,
  getHasAcknowledgedSpanishPrompt,
  getLoanNumber,
  getNhDealApplicationId,
  selectPreferredLanguage,
  setHasAcknowledgedSpanishPrompt,
  setPreferredLanguage,
} from "features/authSlice";
import { isPostLosLoan, selectIsLanguageToggleLocked } from "features/preferredLanguageSlice";
import { selectIsTranslationServiceFailed } from "features/translationServiceSlice";
import { TabType } from "components/v2/spanish-acknowledgment-modal/SpanishAcknowledgmentModal";
import { DEFAULT_LANGUAGE, SPANISH_PROMPT_SOURCE, SPANISH_TRANSLATION_ADOPTION } from "utilities/Constant";
import { deriveApplicationStep } from "utilities/derive-application-step";
import {
  LanguagePreference,
  setFSSpanishTranslationAdoption,
  SpanishPromptSource,
  trackFSLanguagePreferenceChanged,
  trackFSSpanishAcknowledgmentDeclined,
} from "utilities/full-story";
import { useGetFeatureFlagsQuery } from "services/common/featureFlagService";
import {
  buildSyncPreferredLanguageRequest,
  getExplicitUserLanguage,
  getPreferredLanguage,
  hasExplicitUserLanguageSelection,
  hasPreferredLanguageDealSyncCompleted,
  isSpanishLanguage,
  markExplicitUserLanguageSelection,
  markPreferredLanguageDealSyncCompleted,
  normalizeToAppLanguage,
  resolveDisplayedPreferredLanguage,
  selectLoanForPreferredLanguage,
  setPersistedPreferredLanguage,
  toUserDbPreferredLanguage,
} from "utilities/getPreferredLanguage";
import {
  getCookiePreferredLanguage,
  isLangPrefCookieMismatched,
  isRegistrationTranslationDeferPath,
  resolveUnauthenticatedDisplayLanguage,
  syncLangPrefCookie,
} from "utilities/langPrefCookie";
import { useGetMyLoansQuery } from "pages/application/applicationService";
import { useGetBorrowerDetailsQuery } from "pages/dashboard/home/dashboardService";
import { useSyncPreferredLanguageMutation, useUpdateUserPreferredLanguageMutation } from "pages/authentication/userService";
import { reportPreferredLanguageSyncFailure } from "new-relic/new-relic";

interface UsePreferredLanguageOptions {
  readonly isMobile: boolean;
  readonly isAuthenticated: boolean;
}

const refreshPageForTranslation = (): void => {
  globalThis.location.reload();
};

const getErrorStatus = (error: unknown): string => {
  const status = (error as { status?: unknown } | undefined)?.status;
  return typeof status === "number" || typeof status === "string" ? String(status) : "unknown";
};

// 401/403 already dispatch setLogout in baseQueryWithAuth, so retrying would only repeat the logout.
const isAuthFailure = (error: unknown): boolean => {
  const status = getErrorStatus(error);
  return status === "401" || status === "403";
};

export const usePreferredLanguage = ({
  isMobile,
  isAuthenticated,
}: UsePreferredLanguageOptions) => {
  const dispatch = useDispatch();
  const { data: featureFlag } = useGetFeatureFlagsQuery();
  const { pathname } = useLocation();
  const reduxLanguage = useSelector(selectPreferredLanguage);
  const userId = useSelector(getCurrentUserId);
  const dealApplicationId = useSelector(getDealApplicationId);
  const nhDealApplicationId = useSelector(getNhDealApplicationId);
  const hasAcknowledgedSpanishPrompt = useSelector(getHasAcknowledgedSpanishPrompt);
  const [syncPreferredLanguage] = useSyncPreferredLanguageMutation();
  const [updateUserPreferredLanguage] = useUpdateUserPreferredLanguageMutation();
  const [showPostLosLockModal, setShowPostLosLockModal] = useState(false);
  const [showTranslationErrorModal, setShowTranslationErrorModal] = useState(false);
  const [isPersistingPreferredLanguage, setIsPersistingPreferredLanguage] = useState(false);
  const [userLanguage, setUserLanguage] = useState<string>(() =>
    isAuthenticated ? getPreferredLanguage() : resolveUnauthenticatedDisplayLanguage(reduxLanguage, pathname),
  );
  const [showEnglishRevertModal, setShowEnglishRevertModal] = useState(false);
  const [showAcknowledgmentModal, setShowAcknowledgmentModal] = useState(false);
  const [pendingLanguage, setPendingLanguage] = useState<string | null>(null);
  const [spanishPromptSource, setSpanishPromptSource] = useState<SpanishPromptSource | null>(null);
  const learnMoreButtonRef = useRef<HTMLButtonElement>(null);
  const translationLearnMoreButtonRef = useRef<HTMLButtonElement>(null);
  const dealLanguageSeedDoneRef = useRef<string | null>(null);
  const hasLanguageInitializedRef = useRef(false);
  const englishLoPromptHandledRef = useRef(false);
  const currentLoanNumber = useSelector(getLoanNumber);
  const isAllPostLOS = useSelector(selectIsLanguageToggleLocked);
  const isMyLoansPage = pathname.startsWith("/my-loans");
  const hasApplicationContext = Boolean(dealApplicationId?.trim() || nhDealApplicationId?.trim());
  const isCurrentLoanPostLos = isPostLosLoan(currentLoanNumber);
  const isPostLosLoanContext = isMyLoansPage ? isAllPostLOS : isCurrentLoanPostLos;
  const isTranslationFailed = useSelector(selectIsTranslationServiceFailed);
  const isExplicitSelection = hasExplicitUserLanguageSelection();
  const isPreferredLanguageEnabled = Boolean(featureFlag?.preferredLanguageEnabled);

  const { data: myLoansData } = useGetMyLoansQuery(userId, {
    skip: !isAuthenticated || !userId,
  });
  const { data: userProfile } = useGetBorrowerDetailsQuery(userId, {
    skip: !isAuthenticated || !userId,
  });
  const activeLoan = selectLoanForPreferredLanguage(myLoansData, nhDealApplicationId);
  const inApplication = !isMyLoansPage && hasApplicationContext;
  const isPostLosActiveLoan =
    isPostLosLoan(activeLoan?.loanNumber) || (inApplication && isCurrentLoanPostLos);

  const displayLanguage = useMemo(() => {
    if (!isPreferredLanguageEnabled) {
      return DEFAULT_LANGUAGE;
    }

    if (isAuthenticated && inApplication && !activeLoan) {
      return userLanguage;
    }

    const resolved = isAuthenticated
      ? resolveDisplayedPreferredLanguage({
          isExplicitSelection,
          isMyLoansPage,
          hasApplicationContext,
          isPostLosActiveLoan,
          userDbLanguage: userProfile?.preferredLanguage,
          activeLoan,
          reduxLanguage,
          hasAcknowledgedSpanishPrompt,
        })
      : resolveUnauthenticatedDisplayLanguage(reduxLanguage, pathname);

    if (isTranslationFailed && isSpanishLanguage(resolved)) {
      return DEFAULT_LANGUAGE;
    }

    return resolved;
  }, [
    isPreferredLanguageEnabled,
    isTranslationFailed,
    isAuthenticated,
    isExplicitSelection,
    isMyLoansPage,
    hasApplicationContext,
    isPostLosActiveLoan,
    inApplication,
    userProfile?.preferredLanguage,
    activeLoan,
    reduxLanguage,
    userLanguage,
    pathname,
    hasAcknowledgedSpanishPrompt,
  ]);

  const seedUserDbFromDealIfEmpty = useCallback(() => {
    if (
      !activeLoan ||
      !userId ||
      isExplicitSelection ||
      !inApplication ||
      isPostLosActiveLoan ||
      !activeLoan.dealPreferredLanguage?.trim()
    ) {
      return;
    }

    const dealLanguage = toUserDbPreferredLanguage(
      normalizeToAppLanguage(activeLoan.dealPreferredLanguage),
    );
    const userDbLanguage = userProfile?.preferredLanguage?.trim()
      ? toUserDbPreferredLanguage(normalizeToAppLanguage(userProfile.preferredLanguage))
      : null;
    const seedKey = [userId, activeLoan.nhDealApplicationId, dealLanguage].join(":");

    if (userDbLanguage === dealLanguage) {
      dealLanguageSeedDoneRef.current = seedKey;
      return;
    }

    if (!userDbLanguage && dealLanguageSeedDoneRef.current !== seedKey) {
      dealLanguageSeedDoneRef.current = seedKey;
      void updateUserPreferredLanguage({
        userId,
        preferredLanguage: dealLanguage,
      });
    }
  }, [
    activeLoan,
    userId,
    isExplicitSelection,
    inApplication,
    isPostLosActiveLoan,
    userProfile?.preferredLanguage,
    updateUserPreferredLanguage,
  ]);

  useEffect(() => { // NOSONAR
    if (!isPreferredLanguageEnabled) {
      return;
    }

    if (!isAuthenticated) {
      if (displayLanguage !== userLanguage) {
        setUserLanguage(displayLanguage);
      }
      return;
    }

    if (isAuthenticated && userId && myLoansData === undefined) {
      return;
    }

    if (!isExplicitSelection && isMyLoansPage && userProfile === undefined) {
      return;
    }

    if (isAuthenticated && inApplication && !activeLoan) {
      return;
    }

    if (showAcknowledgmentModal || showEnglishRevertModal) {
      return;
    }

    const shouldPromptLoLanguageAck =
      Boolean(activeLoan) &&
      Boolean(activeLoan?.showAcknowledgementModal) &&
      !isPostLosActiveLoan &&
      !isExplicitSelection &&
      !hasAcknowledgedSpanishPrompt;

    const dealLanguage = activeLoan
      ? normalizeToAppLanguage(activeLoan.dealPreferredLanguage)
      : DEFAULT_LANGUAGE;

    if (shouldPromptLoLanguageAck && isSpanishLanguage(dealLanguage)) {
      setPendingLanguage(dealLanguage);
      setSpanishPromptSource(SPANISH_PROMPT_SOURCE.dealSync);
      setShowAcknowledgmentModal(true);
      return;
    }

    if (shouldPromptLoLanguageAck && !isSpanishLanguage(dealLanguage)) {
      englishLoPromptHandledRef.current = true;
      setPendingLanguage(dealLanguage);
      if (!isSpanishLanguage(userLanguage)) {
        if (isSpanishLanguage(getCookiePreferredLanguage())) {
          setUserLanguage(getCookiePreferredLanguage());
        } else if (userProfile?.preferredLanguage?.trim()) {
          setUserLanguage(normalizeToAppLanguage(userProfile.preferredLanguage));
        }
      }
      setShowEnglishRevertModal(true);
      return;
    }

    const shouldPromptEnglishFromLoChange =
      !isExplicitSelection &&
      !isPostLosActiveLoan &&
      !englishLoPromptHandledRef.current &&
      (
        (hasLanguageInitializedRef.current &&
          displayLanguage !== userLanguage &&
          isSpanishLanguage(userLanguage) &&
          !isSpanishLanguage(displayLanguage)) ||
        (isSpanishLanguage(getCookiePreferredLanguage()) && !isSpanishLanguage(displayLanguage))
      );

    if (shouldPromptEnglishFromLoChange) {
      englishLoPromptHandledRef.current = true;
      setPendingLanguage(displayLanguage);
      if (!isSpanishLanguage(userLanguage) && isSpanishLanguage(getCookiePreferredLanguage())) {
        setUserLanguage(getCookiePreferredLanguage());
      }
      setShowEnglishRevertModal(true);
      return;
    }

    if (displayLanguage !== userLanguage) {
      setUserLanguage(displayLanguage);
      if (inApplication && isPostLosActiveLoan) {
        dispatch(setPreferredLanguage(displayLanguage));
      } else if (!isExplicitSelection) {
        setPersistedPreferredLanguage(displayLanguage);
        dispatch(setPreferredLanguage(displayLanguage));
      }
    }

    hasLanguageInitializedRef.current = true;

    if (!isExplicitSelection) {
      seedUserDbFromDealIfEmpty();
    }
  }, [
    displayLanguage,
    userLanguage,
    isAuthenticated,
    isExplicitSelection,
    isPreferredLanguageEnabled,
    isMyLoansPage,
    inApplication,
    isPostLosActiveLoan,
    hasAcknowledgedSpanishPrompt,
    showAcknowledgmentModal,
    showEnglishRevertModal,
    activeLoan,
    myLoansData,
    userId,
    userProfile,
    dispatch,
    seedUserDbFromDealIfEmpty,
  ]);

  useEffect(() => {
    if (!isPreferredLanguageEnabled) {
      return;
    }

    if (!isAuthenticated || !userId || !hasExplicitUserLanguageSelection()) {
      return;
    }
    if (hasPreferredLanguageDealSyncCompleted() || isPersistingPreferredLanguage) {
      return;
    }

    const savedLanguage = getExplicitUserLanguage();
    if (!savedLanguage) {
      return;
    }

    dispatch(setPreferredLanguage(savedLanguage));

    const syncRequest = buildSyncPreferredLanguageRequest(userId, savedLanguage);

    const syncWithRetry = async (): Promise<void> => {
      let response = await syncPreferredLanguage(syncRequest);
      if ("error" in response && !isAuthFailure(response.error)) {
        response = await syncPreferredLanguage(syncRequest);
      }
      if ("error" in response) {
        reportPreferredLanguageSyncFailure(getErrorStatus(response.error));
        return;
      }
      markPreferredLanguageDealSyncCompleted();
    };

    setIsPersistingPreferredLanguage(true);
    void syncWithRetry()
      .catch(() => reportPreferredLanguageSyncFailure("exception"))
      .finally(() => {
        setIsPersistingPreferredLanguage(false);
      });
  }, [
    isAuthenticated,
    userId,
    dispatch,
    syncPreferredLanguage,
    isPreferredLanguageEnabled,
  ]);

  const applyLanguageState = (value: string) => {
    setUserLanguage(value);
    setPersistedPreferredLanguage(value);

    if (isAuthenticated) {
      dispatch(setPreferredLanguage(value));
    }
    syncLangPrefCookie(value);
  };

  const persistLanguageChange = async (value: string): Promise<boolean> => {
    if (!isAuthenticated || !userId) {
      return false;
    }

    const syncRequest = buildSyncPreferredLanguageRequest(userId, value);

    setIsPersistingPreferredLanguage(true);
    try {
      const response = await syncPreferredLanguage(syncRequest);
      const didPersist = !("error" in response);
      if (didPersist) {
        markPreferredLanguageDealSyncCompleted();
      } else {
        setIsPersistingPreferredLanguage(false);
      }
      return didPersist;
    } catch {
      setIsPersistingPreferredLanguage(false);
      return false;
    }
  };

  const commitLanguageChange = async (value: string): Promise<void> => {
    markExplicitUserLanguageSelection();
    const shouldReload =
      isLangPrefCookieMismatched(value) ||
      (isRegistrationTranslationDeferPath(pathname) && isSpanishLanguage(value));
    applyLanguageState(value);

    if (!isAuthenticated) {
      if (shouldReload) {
        refreshPageForTranslation();
      }
      return;
    }

    const didPersist = await persistLanguageChange(value);
    if (didPersist && shouldReload) {
      refreshPageForTranslation();
      return;
    }
    setIsPersistingPreferredLanguage(false);
  };

  const handleLanguageChange = (value: string) => {
    if (isPostLosLoanContext) {
      return;
    }
    setPendingLanguage(value);
    if (value === "Español") {
      setSpanishPromptSource(SPANISH_PROMPT_SOURCE.userToggle);
      setShowAcknowledgmentModal(true);
    } else {
      setShowEnglishRevertModal(true);
    }
  };

  const getTrackingContextProps = () => ({
    applicationStep: deriveApplicationStep(pathname),
    applicationId: dealApplicationId?.trim() ? dealApplicationId : null,
    loanNumber: currentLoanNumber?.trim() ? currentLoanNumber : null,
  });

  const getLanguageTrackingProps = (
    languageToApply: string,
    acknowledgmentLanguage?: TabType,
    promptSource?: SpanishPromptSource,
  ) => {
    const fromLanguage = toUserDbPreferredLanguage(userLanguage) as LanguagePreference;
    const toLanguage = toUserDbPreferredLanguage(languageToApply) as LanguagePreference;

    return {
      fromLanguage,
      toLanguage,
      ...getTrackingContextProps(),
      changeSource: "user_selector" as const,
      acknowledgmentLanguage,
      ...(promptSource ? { promptSource } : {}),
    };
  };

  const handleAcknowledge = (acknowledgmentLanguage: TabType) => {
    const languageToApply = pendingLanguage ?? "Español";
    trackFSLanguagePreferenceChanged(
      getLanguageTrackingProps(
        languageToApply,
        acknowledgmentLanguage,
        spanishPromptSource ?? SPANISH_PROMPT_SOURCE.userToggle,
      ),
    );
    setFSSpanishTranslationAdoption(SPANISH_TRANSLATION_ADOPTION.adopted);
    setPendingLanguage(null);
    setSpanishPromptSource(null);
    if (isAuthenticated) {
      dispatch(setHasAcknowledgedSpanishPrompt(true));
    }
    setShowAcknowledgmentModal(false);
    void commitLanguageChange(languageToApply);
  };

  const trackSpanishAcknowledgmentDeclined = () => {
    const promptSource = spanishPromptSource ?? SPANISH_PROMPT_SOURCE.userToggle;
    const dealPreferredLanguage = activeLoan?.dealPreferredLanguage?.trim()
      ? (toUserDbPreferredLanguage(normalizeToAppLanguage(activeLoan.dealPreferredLanguage)) as LanguagePreference)
      : null;

    trackFSSpanishAcknowledgmentDeclined({
      fromLanguage: toUserDbPreferredLanguage(userLanguage) as LanguagePreference,
      dealPreferredLanguage,
      ...getTrackingContextProps(),
      promptSource,
    });

    if (promptSource === SPANISH_PROMPT_SOURCE.dealSync) {
      setFSSpanishTranslationAdoption(SPANISH_TRANSLATION_ADOPTION.declined);
    }
  };

  const handleAcknowledgmentCancel = async () => {
    const languageToRetain = userLanguage;
    trackSpanishAcknowledgmentDeclined();
    setShowAcknowledgmentModal(false);
    setPendingLanguage(null);
    setSpanishPromptSource(null);
    markExplicitUserLanguageSelection();

    if (!isAuthenticated || !userId) {
      applyLanguageState(languageToRetain);
      return;
    }

    dispatch(setHasAcknowledgedSpanishPrompt(true));
    applyLanguageState(languageToRetain);
    await persistLanguageChange(languageToRetain);
    setIsPersistingPreferredLanguage(false);
  };

  const handleRevertConfirm = () => {
    const languageToApply = pendingLanguage ?? DEFAULT_LANGUAGE;
    trackFSLanguagePreferenceChanged(getLanguageTrackingProps(languageToApply));
    setPendingLanguage(null);
    setShowEnglishRevertModal(false);
    void commitLanguageChange(languageToApply);
  };

  const handleRevertCancel = async () => {
    const languageToRetain = userLanguage;
    setPendingLanguage(null);
    setShowEnglishRevertModal(false);
    markExplicitUserLanguageSelection();
    applyLanguageState(languageToRetain);

    if (!isAuthenticated || !userId) {
      return;
    }

    await persistLanguageChange(languageToRetain);
    setIsPersistingPreferredLanguage(false);
  };

  const handleClosePostLosLockModal = () => {
    setShowPostLosLockModal(false);
  };

  const getLockedClickHandler = (): (() => void) | undefined => {
    if (isMobile && isTranslationFailed) {
      return () => setShowTranslationErrorModal(true);
    }
    if (isMobile && isPostLosLoanContext) {
      return () => setShowPostLosLockModal(true);
    }
    return undefined;
  };

  return {
    isPreferredLanguageEnabled,
    userLanguage,
    isPostLosLoanContext,
    isTranslationFailed,
    isPersistingPreferredLanguage,
    showPostLosLockModal,
    showTranslationErrorModal,
    showEnglishRevertModal,
    showAcknowledgmentModal,
    learnMoreButtonRef,
    translationLearnMoreButtonRef,
    handleLanguageChange,
    handleAcknowledge,
    handleAcknowledgmentCancel,
    handleRevertConfirm,
    handleRevertCancel,
    handleClosePostLosLockModal,
    setShowPostLosLockModal,
    setShowTranslationErrorModal,
    getLockedClickHandler,
  };
};
